// src/components/MyAttendance.jsx — the attendance board, as an agent sees it.
//
// Owner, need-care list 2026-09-02: "we also need to add attendance tab for agents too. here
// we should cap it only to attendance selections, no further down should be shown. every
// agent can only access and select their arrival in the office… agents can[']t press someone
// else's attendance status. and they can press left from anywhere. let's make them not edit
// their presses — if they press left they can contact manager to correct it."
//
// ⚠️ THIS FILE ARGUED THE OPPOSITE UNTIL LATER THAT DAY, and the owner overruled it. It used to
// say: "A SEPARATE COMPONENT, NOT A NARROWED AttendanceTab … 'cap it to attendance selections'
// is not a filter over that surface, it is a different surface." So it shipped one card showing
// only the viewer's own two times. The owner put that beside the manager's grid and asked: "this
// is what agents sees and can it see full cars list? same UI design just limited functionality
// just one day."
//
// The ruling is better than the argument it replaced, and it is worth saying why rather than
// just complying: the question an agent actually has at 11am is who else is in, and the old card
// could not answer it. What the original note got right was narrower than it claimed — the
// manager grid discloses colleagues' work phones and districts on hover, and that is
// management's tool for chasing someone who is late. So the disclosure comes off (DayView's
// `viewer` prop); the grid stays.
//
// ⚠️ WHAT ENFORCES WHAT, unchanged. The two presses go through database functions that resolve
// the agent from the SESSION and the location from the EDGE (0002), and since 0003 an agent
// cannot write `attendance` directly at all. So the grid's quick In/Out CANNOT use the manager's
// `onSave` path — it would be refused, correctly. `saveOwn` below routes it to the same two
// functions. Nothing on this screen is trusted; what it owns is telling the truth about the
// answer BEFORE the press.
//
// ⚠️ ONE DAY, TODAY, no navigation — "just one day". A date picker here would invite an agent
// into last month's roster, which is a manager's view rather than a self-service one.
import { useEffect, useMemo, useState } from 'react'
import { t } from '../lib/i18n.js'
import { Icon } from './Icon.jsx'
import { DayView } from './AttendanceTab.jsx'
import { MyPhonePanel } from './MyPhonePanel.jsx'
// ⚠️ The fetching and the two acts live in useOwnAttendance so that this board and the shell's
// ShiftPrompt cannot disagree about whether today is already marked. A banner reading "you have
// not arrived" over a card reading "arrived 09:12" is the contradiction the shared hook exists
// to make impossible.
import { useOwnAttendance } from '../hooks/useOwnAttendance.js'
import { getAttendance, getScheduleHistory } from '../db/index.js'
import { MyWorkerSwitch } from './MyWorkerSwitch.jsx'
import { todayStr } from '../lib/clock.js'

// ── MY COMPUTER WORKS FOR ME (core 3662, owner 2026-10-20: "allow agents to select worker to pause or continue from
// their dashboard"). Automatic = paused while the agent is marked in the office (3660), running after; Pause / Keep
// running override it. The server decides; this only shows the state and sends the choice.
export function MyAttendance({ agent, agents = [], overrideMap = {}, lang }) {
  const agentId = agent?.id || null
  const date = todayStr()
  const att = useOwnAttendance(agentId)
  const [rows, setRows] = useState(null)
  // 🔴 THE REAL SCHEDULE HISTORY, not []. This was `scheduleHistory={[]}` on the theory that
  // scheduledSlotsOnDate "falls back to the agent's CURRENT schedule for today, so one day
  // needs no history". Measured on the live screen 2026-09-03: with no history EVERY agent
  // rendered as scheduled 11:00–19:00 and "+28m late" — 30 of them — while the manager's view
  // of the same day showed 5 scheduled and the rest on a day off. The fallback does not know
  // who is off today; only the history does. That fake 30-strong grid is the "full list" the
  // owner kept objecting to. Fails soft to [] so a read error degrades to the old screen
  // rather than to no screen.
  const [hist, setHist] = useState([])
  useEffect(() => {
    let gone = false
    getScheduleHistory().then(h => { if (!gone) setHist(h || []) }).catch(() => { if (!gone) setHist([]) })
    return () => { gone = true }
  }, [date])

  // Everyone's row for TODAY. Reads are deliberately open under 0003 — Stats and the Desk both
  // aggregate the whole roster, so narrowing SELECT would return zeros rather than errors — and
  // only the writes are gated. Re-runs once either of the viewer's own presses lands.
  useEffect(() => {
    let gone = false
    getAttendance({ dateFrom: date, dateTo: date })
      .then(r => { if (!gone) setRows(r || []) })
      .catch(() => { if (!gone) setRows([]) })
    return () => { gone = true }
  }, [date, att.checkIn, att.checkOut])

  const attendanceByDate = useMemo(() => {
    const out = {}
    for (const r of rows || []) (out[r.agent_id] ||= {})[r.date] = r
    return out
  }, [rows])

  if (!agentId) {
    // A signed-in person with no roster row — an admin, or an agent not yet linked. Saying so
    // beats an empty screen with a dead button (the shape this codebase keeps recording).
    return (
      <div className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
        <Icon name="alert-triangle" size={14} /> {t('my_att_no_agent_row')}
      </div>
    )
  }

  // ⚠️ The grid's quick buttons hand us a whole attendance ROW and we ignore nearly all of it.
  // The agent does not get to say who they are, what time it is, or what their status becomes —
  // the database decides all three. Only WHICH ACT was pressed is read off the payload.
  const saveOwn = async (record) => {
    if (record.check_in && !att.checkIn) await att.arrive()
    else if (record.check_out && !att.checkOut) await att.leave()
  }

  return (
    <div className="space-y-3">
      {/* WHERE THEY ARE, above the board rather than after a refused press. `office_enforces`
          false is said out loud: the permission then comes from the office having no addresses
          configured, not from a check that passed. */}
      {att.loc && (
        <div className={`text-xs rounded-lg px-3 py-2 ${
          att.loc.ok === false ? 'bg-amber-500/10 text-amber-700 dark:text-amber-400'
            : 'bg-muted/40 text-muted-foreground'}`}>
          <Icon name={att.loc.ok === false ? 'alert-triangle' : 'building'} size={12} />{' '}
          {att.loc.ok === false
            ? t(`my_att_loc_${att.loc.reason}`)
            : att.loc.office_enforces ? t('my_att_loc_in_office') : t('my_att_loc_unchecked')}
        </div>
      )}

      {/* Directly under the verdict it explains. When the office refused because it could not
          see their phone, the fix is one control away instead of a support conversation. */}
      <MyPhonePanel
        agentId={agentId}
        relevant={att.loc?.reason === 'device_not_seen' || att.loc?.reason === 'device_on_office_wifi'}
      />

      <MyWorkerSwitch />

      {att.err && <p className="text-xs text-destructive">{att.err}</p>}

      {/* 🔑 THE RULE, owner 2026-09-03, after this view was wrong four times in one day:
          "this is what admin sees and this is the list there must be." The agent sees the SAME
          day as the manager — the same active people, the same day-off greys, the same summary
          — and may act only on their own card. Two things and only two are different:
          • the ARCHIVE is out ("agent must not be able to see archives — past agents"): a
            resigned colleague is neither drawn nor counted;
          • nothing opens, and the check-in buttons live on their own card only (DayView's
            restricted mode).
          Every earlier "fix" here — hiding colleagues, hiding lateness, hiding the summary — was
          me solving a different complaint than the one made. The complaint was a FAKE list:
          with no schedule history every agent read as scheduled at 11:00, so the agent saw 30
          "late" colleagues where the manager saw 5 working. The history above is the fix. */}
      <DayView
        // The ACTIVE roster: the day summary counts it, DayView draws only your card.
        // 🔴 NEVER the archive. Owner 2026-09-03: "agent must not be able to see archives —
        // past agents." Resigned people reach this prop (App passes filteredAgents unfiltered;
        // only ScheduleTab strips them), and a former colleague has no business in an agent's
        // headcount, let alone on their screen. The manager view keeps them, greyed, on purpose.
        agents={agents.filter(a => a.is_active !== false)}
        date={date}
        attendanceByDate={attendanceByDate}
        onOpenModal={() => {}}
        onSave={saveOwn}
        lang={lang}
        scheduleHistory={hist}
        // The candidate row stays empty: a hiring surface an agent has no business seeing.
        overrideMap={overrideMap}
        expectedCandidates={[]}
        viewer={{ agentId }}
      />

      {/* Said once, plainly, rather than discovered by pressing: these are recorded, not
          edited. The owner's rule — "if they press left they can contact manager to correct
          it" — is only fair if the agent is told before they press. */}
      <p className="text-[11px] text-muted-foreground">{t('my_att_once_note')}</p>
    </div>
  )
}
